import React, { Component } from 'react'
import { toast } from 'react-toastify'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'

// REDUX
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as commonActions from '../../../actions/commonActions'
import * as usersActions from '../../../actions/usersActions'
import * as userActions from '../../../actions/userActions'
import * as authActions from '../../../actions/authActions'

// CHILDREN
import UserRow from '../UserRow'
import AdminUserEditModal from '../Modals/AdminUserEditModal'
import PageHeader from '../../common/PageHeader/PageHeader'
import Placeholder from '../../common/Placeholder'
import Loader from '../../common/loader/legacy-loaders/Op36Loader-web'
import SearchInput from '../../common/SearchInput'

// ASSETS
import './AdminUsersPage.scss'
import { debounce } from '../../../utility/debounceHelper'

class AdminUsersPage extends Component {
  state = {
    users: [],
    usersLoader: false,
    searchTerm: '',
    showEditModal: false,
    updateUserLoader: false,
    deleteUserLoader: false,
  }

  componentDidMount() {
    const { common_actions } = this.props
    common_actions.setCurrentTab('Users')
    common_actions.showSideBar(true)
    common_actions.showNavBar(true)
  }

  fetchUser = user => {
    const { user_actions } = this.props
    this.setState(
      {
        userLoader: true,
      },
      () => {
        user_actions.getUser(user.id, 'admin').then(() => {
          this.setState({
            userLoader: false,
          })
        })
      }
    )
  }

  updateUser = user => {
    const { user_actions } = this.props

    const allowedTypes = ['Coach', 'Student', 'Representative']
    if (!allowedTypes.includes(user.type)) {
      return null
    }

    this.setState({ userLoader: true, updateUserLoader: true })

    const cleanUser = {
      id: user.id,
      type: user.type,
      family_id: user.family_id,
      do_not_track: user.do_not_track,
    }

    if (user.type === 'Representative') {
      cleanUser.bio = user.bio
      cleanUser.booking_url = user.booking_url
    }

    if (user.type === 'Coach') {
      cleanUser.pipedrive_person_id = user.pipedrive_person_id
    }

    const { t } = this.props
    user_actions.updateUser(cleanUser).then((result) => {
      this.setState({ searchTerm: '', updateUserLoader: false, userLoader: false })
      this.closeEditModal()
      if (result.type === 'UI_ERROR') {
        if (result.status === 503) {
          toast.error(t('users.page.toast.updateUnavailable'), {
            position: toast.POSITION.BOTTOM_LEFT,
          })
        } else {
          toast.error(
            result.error?.base?.[0] || t('users.page.toast.updateError', { name: `${user.first_name} ${user.last_name}` }),
            { position: toast.POSITION.BOTTOM_LEFT }
          )
        }
      } else {
        toast.success(t('users.page.toast.updateSuccess', { name: `${user.first_name} ${user.last_name}` }), {
          position: toast.POSITION.BOTTOM_LEFT,
        })
      }
    })
  }

  deleteUser = user => {
    const { user_actions, t } = this.props
    this.setState({ deleteUserLoader: true })

    user_actions.adminDeleteUser(user.id).then(() => {
      this.setState({
        searchTerm: '',
        deleteUserLoader: false,
        users: [...this.props.users],
      })
      if (_.isEmpty(this.props.error)) {
        this.closeEditModal()
        toast.success(t('users.page.toast.deleteSuccess', { name: `${user.first_name} ${user.last_name}` }), {
          position: toast.POSITION.BOTTOM_LEFT,
        })
      } else {
        toast.error(
          t('users.page.toast.deleteError', { name: `${user.first_name} ${user.last_name}` }),
          {
            position: toast.POSITION.BOTTOM_LEFT,
          }
        )
      }
    })
  }

  toggleEditModal = user => {
    this.setState(
      {
        showEditModal: true,
      },
      () => {
        this.fetchUser(user)
      }
    )
  }

  closeEditModal = () => {
    this.setState({
      showEditModal: false,
    })
  }

  resetState = () => {
    this.setState({
      users: [],
      usersLoader: false,
    })
  }

  searchUsers = () => {
    const { searchTerm } = this.state

    if (searchTerm.length < 5) {
      return
    }

    this.setState({ usersLoader: true })
    this.props.users_actions.searchUsers(searchTerm).then(() => {
      this.setState({
        users: [...this.props.users],
        usersLoader: false,
      })
    })
  }

  debouncedFetchUsers = debounce(this.searchUsers)

  searchUpdated = e => {
    const term = e.target.value

    if (!term) {
      this.resetState()
      return
    }

    this.setState({ searchTerm: term }, () => {
      this.debouncedFetchUsers()
    })
  }

  renderUsers = () => {
    const { t } = this.props
    const users = this.state.users

    if (users.length === 0) {
      return (
        <div className='col-md-6 mx-auto'>
          <Placeholder
            title={t('users.page.noResults')}
            icon={'fa-search'}
            subtext1={t('users.page.noResultsSubtext')}
          />
        </div>
      )
    }

    return users.map(user => (
      <UserRow user={user} toggleModal={this.toggleEditModal} key={user.id} />
    ))
  }

  renderUserTable = () => {
    const { t } = this.props
    const { users } = this.state
    if (users && users.length > 0) {
      return (
        <div className='container-fluid mx-auto'>
          <div className='row w-100 users-table-header'>
            <div className='col-1'>
              <p>{t('users.page.col.id')}</p>
            </div>
            <div className='col-3'>
              <p>{t('common.field.name')}</p>
            </div>
            <div className='col-1'>
              <p>{t('users.page.col.type')}</p>
            </div>
            <div className='col-3'>
              <p>{t('users.page.col.emailUsername')}</p>
            </div>
            <div className='col-3'>
              <p>{t('users.page.col.community')}</p>
            </div>
            <div className='col-1'>
              <p>{t('users.page.col.idpId')}</p>
            </div>
          </div>
          <div>{this.renderUsers()}</div>
        </div>
      )
    } else {
      return (
        <div className='col-md-6 mx-auto'>
          <Placeholder
            title={t('users.page.searchForUser')}
            icon={'fa-search'}
            subtext1={t('users.page.searchSubtext')}
          />
        </div>
      )
    }
  }

  render() {
    const {
      usersLoader,
      showEditModal,
      userLoader,
      updateUserLoader,
      deleteUserLoader,
    } = this.state
    const { user, error, t } = this.props

    return (
      <div id='AdminUsersPage'>
        <div className='animated fadeIn'>
          <PageHeader title={t('users.page.title')} />
          <div className='users-search-bar mt-2'>
            <SearchInput placeholder={t('users.page.searchPlaceholder')} onChange={this.searchUpdated}/>
          </div>
          <div className='row'>
            {usersLoader ? (
              <Loader message={t('users.page.loadingUsers')} />
            ) : (
              this.renderUserTable()
            )}
          </div>
        </div>
        {showEditModal && (
          <AdminUserEditModal
            user={user}
            error={error}
            updateUser={this.updateUser}
            deleteUser={this.deleteUser}
            showModal={showEditModal}
            closeModal={this.closeEditModal}
            loader={userLoader}
            updateLoader={updateUserLoader}
            deleteLoader={deleteUserLoader}
          />
        )}
      </div>
    )
  }
}

AdminUsersPage.propTypes = {
  t: PropTypes.func,
  user: PropTypes.object,
  users: PropTypes.array,
  error: PropTypes.object,
  common_actions: PropTypes.shape({
    showSideBar: PropTypes.func,
    showNavBar: PropTypes.func,
    setCurrentTab: PropTypes.func,
  }),
  user_actions: PropTypes.shape({
    adminDeleteUser: PropTypes.func,
    updateUser: PropTypes.func,
    getUser: PropTypes.func,
  }),
  users_actions: PropTypes.shape({
    searchUsers: PropTypes.func,
  }),
}

function mapStateToProps(state) {
  return {
    ui: state.ui,
    user: state.user.user,
    current_user: state.user.current_user,
    users: state.users,
    error: state.ui.error || {},
  }
}

function mapDispatchToProps(dispatch) {
  return {
    common_actions: bindActionCreators(commonActions, dispatch),
    users_actions: bindActionCreators(usersActions, dispatch),
    user_actions: bindActionCreators(userActions, dispatch),
    auth_actions: bindActionCreators(authActions, dispatch),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AdminUsersPage))
